import React, { useState, useEffect, useRef } from 'react';
import { VendorRecord, VendorStatusKey } from '@/types/domain/vendor-management';
import { Button } from '@/components/ui/Button';
import { AlertTriangle, X } from 'lucide-react';

interface VendorActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newStatus: VendorStatusKey, reason: string) => Promise<void>;
  vendor: VendorRecord;
  targetAction: 'APPROVE' | 'SUSPEND' | 'BLOCK' | 'REJECT';
  isSubmitting?: boolean;
}

export const VendorActionModal: React.FC<VendorActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  vendor,
  targetAction,
  isSubmitting = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const getStatusKey = (): VendorStatusKey => {
    switch (targetAction) {
      case 'APPROVE':
        return 'APPROVED';
      case 'SUSPEND':
        return 'SUSPENDED';
      case 'BLOCK':
        return 'BLOCKED';
      case 'REJECT':
        return 'REJECTED';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (targetAction !== 'APPROVE' && !reason.trim()) {
      setError(`Mandatory justification reason required to ${targetAction.toLowerCase()} vendor.`);
      return;
    }
    setError(null);
    await onConfirm(getStatusKey(), reason.trim() || 'Approved by Procurement Admin');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !isSubmitting && onClose()} />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className="relative z-50 w-full max-w-lg rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95"
      >
        <div className="flex items-start justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div
              className={`p-3 rounded-2xl border ${
                targetAction === 'APPROVE'
                  ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                  : targetAction === 'SUSPEND'
                  ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                  : 'bg-rose-500/10 text-rose-600 border-rose-500/20'
              }`}
            >
              <AlertTriangle className="h-5 w-5" />
            </div>

            <div>
              <h3 className="font-extrabold text-base text-foreground capitalize">
                {targetAction.toLowerCase()} Vendor Master
              </h3>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5">
                {vendor.vendorCode} • <strong className="text-foreground">{vendor.name}</strong>
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-8 w-8 p-0 rounded-full text-foreground-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {targetAction !== 'APPROVE' && (
            <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-800 space-y-1">
              <span className="font-bold block">Operational Impact Notice:</span>
              <p className="text-[11px] text-amber-700">
                {targetAction === 'SUSPEND' && 'Suspending this supplier will restrict participation in future RFQs and Purchase Orders.'}
                {targetAction === 'BLOCK' && 'Blocking this supplier will immediately halt all active sourcing requests and PO generation.'}
                {targetAction === 'REJECT' && 'Rejecting vendor onboarding will archive the request record.'}
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 font-bold">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label className="font-bold text-foreground block">
              Justification Reason {targetAction !== 'APPROVE' ? '*' : '(Optional)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={`Enter justification reason for ${targetAction.toLowerCase()} action...`}
              className="w-full rounded-xl border border-border bg-background p-3 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" type="button" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              className={`rounded-xl font-bold text-white ${
                targetAction === 'APPROVE'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : targetAction === 'SUSPEND'
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-rose-600 hover:bg-rose-700'
              }`}
            >
              {isSubmitting ? 'Updating...' : `Confirm ${targetAction}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
